import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { EntitySection } from '../entity/EntitySection';
import type { EntitySectionConfig, RenderItem } from '../entity/entity';

import { SortableItem } from './SortableItem';

type Props<T> = {
  section: EntitySectionConfig<T>;
  getId: (item: T) => string | number;
  renderItem: (
    item: T,
    dragHandleProps: React.HTMLAttributes<HTMLElement> & {
      handleRef: (node: HTMLElement | null) => void;
    }
  ) => React.ReactNode;
  onReorder: (sectionKey: string, items: T[]) => void;
  emptyLabel?: string;
  emptyDescription?: string;
  showEmptyState?: boolean;
};

export const SortableSection = <T,>({
  section,
  getId,
  renderItem,
  onReorder,
  emptyLabel,
  emptyDescription,
  showEmptyState = true,
}: Props<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const itemIds = section.items.map((item) => String(getId(item)));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = itemIds.indexOf(String(active.id));
    const newIndex = itemIds.indexOf(String(over.id));

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    onReorder(section.key, arrayMove(section.items, oldIndex, newIndex));
  };

  const sortableRenderItem: RenderItem<T> = (item) => (
    <SortableItem key={getId(item)} id={getId(item)}>
      {(dragHandleProps) => renderItem(item, dragHandleProps)}
    </SortableItem>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <EntitySection
          section={section}
          renderItem={sortableRenderItem}
          emptyLabel={emptyLabel}
          emptyDescription={emptyDescription}
          showEmptyState={showEmptyState}
        />
      </SortableContext>
    </DndContext>
  );
};
